import { Icon, type IconName } from '@starter/ui';
import { Link, type LinkProps } from '@tanstack/react-router';
import { doorArtwork } from '../artwork.ts';

/**
 * A door: a way into making something.
 *
 * Not `OptionCard`, and the difference is the whole reason this is its own
 * component. An option is a choice — it stays on the page, takes the brand
 * tint when picked, and the page waits for you. A door leaves. Nothing here is
 * ever selected, nothing is ever tinted, and the arrow in the title row is the
 * mark that tells the two apart at a glance.
 *
 * Measured off the original in `scratchpad/block-launcher`: 320x300, white,
 * radius 16, a *one* pixel border — an entity card's is two, and a door in a
 * row of two would look armoured at that — padding 16, gap 16, media at 286x189
 * on the muted fill, title 14 with the arrow pushed out, body 14/16.8.
 */
export interface LauncherProps {
  title: string;
  description: string;
  icon: IconName;
  /** Where it goes. A door with nowhere to go says so instead. */
  to?: LinkProps['to'];
  search?: LinkProps['search'];
  disabled?: boolean;
  testId?: string;
  /**
   * A `job_kinds` key, when this door has artwork for its format. Absent, the
   * icon is shown instead — a key can arrive by migration before its picture
   * does, and that is not an error (`src/artwork.ts`).
   */
  artKey?: string;
}

export function Launcher({
  title,
  description,
  icon,
  to,
  search,
  disabled,
  testId,
  artKey,
}: LauncherProps) {
  const art = artKey ? doorArtwork(artKey) : undefined;
  const body = (
    <>
      {/*
        The original puts a still from the format here, and this slot held an
        icon instead — on the grounds that we have no stills, nothing has been
        generated by anybody, and a photograph here would promise a result we
        did not make.

        That argument was right about the promise and wrong about the picture.
        A door has to say *what this format is*, and an illustration of a
        format is not a claim about your output. The difference is now stated
        outright rather than sidestepped (`prd/DIVERGENCE.md`); the artwork is
        ours, generated, and deliberately generic.
      */}
      <span className="relative flex aspect-[286/189] w-full items-center justify-center overflow-hidden rounded-md bg-surface-muted text-ink-faint">
        {art ? (
          <img alt="" className="absolute inset-0 size-full object-cover" src={art} />
        ) : (
          <Icon name={icon} size={32} />
        )}
      </span>
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[14px] leading-[21px] font-semibold text-ink">
          {title}
        </span>
        <Icon className="flex-none text-ink" name="arrow-right" size={18} />
      </span>
      <span className="text-[14px] leading-[16.8px] text-ink">{description}</span>
    </>
  );

  const className =
    'flex w-[320px] flex-none flex-col gap-4 rounded-card border border-border-subtle bg-surface-raised p-4 text-left shadow-button transition-colors';

  if (disabled || !to) {
    return (
      <span
        aria-disabled="true"
        className={`${className} cursor-not-allowed opacity-55`}
        data-fidelity="launcher"
        data-testid={testId}
      >
        {body}
      </span>
    );
  }

  return (
    <Link
      className={`${className} cursor-pointer hover:border-brand-300`}
      data-fidelity="launcher"
      data-testid={testId}
      search={search ?? {}}
      to={to}
    >
      {body}
    </Link>
  );
}

/** `items-stretch` is why a two-line description does not make one door taller. */
export function LauncherRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-stretch gap-5" data-fidelity="launcher-row">
      {children}
    </div>
  );
}
